import React from 'react';
import { Link } from 'react-router-dom';
import { Modal, Table } from 'react-bootstrap';
import { getActivities } from '../api/trainer';
import { Activity, SummaryGranularity } from '../api/types';
import StatusMessage from './StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { activityTypeLabel, formatDate, formatDistance, formatDuration, formatPace } from '../utils/format';
import { VolumeRow, periodEnd } from '../utils/volume';

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

/**
 * Every activity of one period, from GET /activities with from and to. The endpoint filters by one type, so the
 * run types from the summary are applied here. Newest first, as the endpoint returns them.
 */
async function runsInPeriod(from: string, to: string, types: string[]): Promise<Activity[]> {
  const all: Activity[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await getActivities({ from, to, page, size: PAGE_SIZE });
    all.push(...result.items.filter(a => a.activityType !== null && types.includes(a.activityType)));
    if (page + 1 >= result.totalPages) break;
  }
  return all;
}

/** Lists the runs of one bar (or table row) and links each one to its activity. */
const VolumeBucketModal: React.FC<{
  granularity: SummaryGranularity;
  row: VolumeRow;
  types: string[];
  onHide: () => void;
}> = ({ granularity, row, types, onHide }) => {
  const to = periodEnd(granularity, row.bucketStart);
  const runs = useAsync(() => runsInPeriod(row.bucketStart, to, types), [row.bucketStart, granularity]);

  return (
    <Modal show onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Runs · {row.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="muted small mb-3">
          {row.runs} {row.runs === 1 ? 'run' : 'runs'}, {row.distanceKm.toFixed(1)} km, running only
        </p>
        <StatusMessage loading={runs.loading} error={runs.error} onRetry={runs.reload} />
        {runs.data && runs.data.length === 0 && <p className="muted mb-0">No runs in this period.</p>}
        {runs.data && runs.data.length > 0 && (
          <Table size="sm" className="trainer-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Activity</th>
                <th className="text-end">Distance</th>
                <th className="text-end">Pace</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map(a => (
                <tr key={a.id}>
                  <td className="tabular">{formatDate(a.startTime)}</td>
                  <td>
                    <Link to={`/activities/${a.id}`} onClick={onHide}>
                      {a.name ?? 'Untitled activity'}
                    </Link>
                    <div className="stat-hint">{activityTypeLabel(a.activityType)} · {formatDuration(a.durationS)}</div>
                  </td>
                  <td className="text-end tabular">{formatDistance(a.distanceM)}</td>
                  <td className="text-end tabular">{formatPace(a.avgPaceSPerKm)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default VolumeBucketModal;
